// Utility function to convert decimal to percentage with proper rounding
// Prevents floating-point precision issues (e.g., 0.07 * 100 = 7.000000000000001)
const decimalToPercentage = (decimal) => {
	if (!decimal && decimal !== 0) return 0;
	return Math.round(decimal * 100 * 100) / 100;
};

// Normalize data retrieved from the backend for the front-end
// This function converts all monetary values to human readable / editable format
// For example, 1000 cents becomes $10.00
export const normalizeWorkflowForView = (workflow) => {
	if (!workflow) return null; // Prevent errors for undefined values

	const normalizedWorkflow = {
		...workflow,
		discountPercent:
			workflow.discountPercent !== undefined
				? decimalToPercentage(workflow.discountPercent)
				: 0,
		taxPercent: workflow.taxPercent ? workflow.taxPercent * 100 : 0,
		discountAmount: workflow.discountAmount
			? workflow.discountAmount / 100
			: 0,
		taxAmount: workflow.taxAmount ? workflow.taxAmount / 100 : 0,
		total: workflow.total ? workflow.total / 100 : 0,
		refundedAmount: workflow.refundedAmount
			? workflow.refundedAmount / 100
			: workflow.refundedAmount,
		lineItems: workflow.lineItems
			? normalizeLineItemsForView(workflow.lineItems)
			: [],
		taxLines: workflow.taxLines
			? normalizeTaxLinesForView(workflow.taxLines)
			: [],
	};

	return normalizedWorkflow;
};

// Prepare data to be sent to the backend
// This function converts all monetary values to cents
// For example, $10.00 becomes 1000 cents
export const prepareWorkflowForBackend = (workflow) => {
	if (!workflow) return null; // Prevent errors for undefined values

	return {
		...workflow,
		discountPercent: workflow.discountPercent
			? workflow.discountPercent / 100
			: 0,
		taxAmount: workflow.taxAmount
			? Math.round(workflow.taxAmount * 100)
			: 0,
		taxLines: workflow.taxLines
			? prepareTaxLinesForBackend(workflow.taxLines)
			: [],
		discountAmount: workflow.discountAmount
			? Math.round(workflow.discountAmount * 100)
			: 0,
		lineItems: workflow.lineItems
			? prepareLineItemsForBackend(workflow.lineItems)
			: [],
		total: workflow.total ? Math.round(workflow.total * 100) : 0,
		refundedAmount:
			workflow.refundedAmount != null
				? Math.round(workflow.refundedAmount * 100)
				: workflow.refundedAmount,
	};
};

// Normalize line item unitPrice and total to human readable format from the backend
export const normalizeLineItemsForView = (lineItems) => {
	return lineItems.map((item) => ({
		...item,
		unitPrice: item.unitPrice ? item.unitPrice / 100 : 0, // 1000 cents -> $10.00
		total: item.total ? item.total / 100 : 0, // 1000 cents -> $10.00
	}));
};

// Prepare line item unitPrice and total to cents for the backend
export const prepareLineItemsForBackend = (lineItems) => {
	return lineItems.map((item) => ({
		...item,
		unitPrice: item.unitPrice ? item.unitPrice * 100 : 0, // $10.00 -> 1000 cents
		total: item.total ? item.total * 100 : 0, // $10.00 -> 1000 cents
	}));
};

// Prepare tax line amount and rate to cents for the backend
export const prepareTaxLinesForBackend = (taxLines) => {
	return taxLines.map((taxLine) => ({
		...taxLine,
		amount: taxLine.amount ? Math.round(taxLine.amount * 100) : 0, // $10.00 -> 1000 cents
		rate: taxLine.rate ? taxLine.rate / 100 : 0, // Convert to decimal 5% -> 0.05
	}));
};

// Normalize tax line amount and rate to human readable format from the backend
export const normalizeTaxLinesForView = (taxLines) => {
	return taxLines.map((taxLine) => ({
		...taxLine,
		amount: taxLine.amount ? taxLine.amount / 100 : 0,
		rate: taxLine.rate ? decimalToPercentage(taxLine.rate) : 0, // Convert to percentage with proper rounding: 0.05 -> 5.00%
	}));
};
